import { Icon, Text } from "@ttc3k/trekker";
import { AssetImage } from "gql/generated";
import { BoxIso } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { SmartImage } from "shared/components/atoms";
import { sva } from "styled-system/css";
import { Flex } from "styled-system/jsx";

type HeaderImageProps = {
  image?: Partial<AssetImage> | null;
  type: "OWNER" | "EDITOR" | "VIEW_ONLY" | "NO_ACCESS" | "DEFAULT";
  size?: "lg" | "sm";
  doNotShowFallback?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  DefaultIcon?: React.ForwardRefExoticComponent<any>;
  isLogoImage?: boolean;
  isGrayscale?: boolean;
  doNotShowTag?: boolean;
  smartFill?: boolean;
};

const defaultImageStyles = sva({
  slots: ["root", "tag", "icon"],
  base: {
    root: {
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "100",
      overflow: "hidden",
      w: "full",
      position: "relative",
    },
    tag: {
      position: "absolute",
      top: "0",
      left: "0",
      borderBottomRightRadius: "100",
      px: "150",
      py: "10px",
      color: "white",
      h: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  variants: {
    visual: {
      OWNER: {
        root: {
          bg: "bg.accent.blue.lightest",
        },
        icon: {
          color: "icon.accent.blue.light",
        },
        tag: {
          bg: "bg.accent.blue.dark",
          px: "200",
        },
      },
      EDITOR: {
        root: {
          bg: "bg.accent.blue.lightest",
        },
        icon: {
          color: "icon.accent.blue.light",
        },
        tag: {
          bg: "bg.accent.blue.light",
          color: "text.accent.blue.dark",
          px: "200",
        },
      },
      VIEW_ONLY: {
        root: {
          bg: "bg.accent.blue.lightest",
        },
        icon: {
          color: "icon.accent.blue.light",
        },
        tag: {
          bg: "bg.accent.blue.light",
          color: "text.accent.blue.dark",
          px: "200",
        },
      },
      NO_ACCESS: {
        root: {
          bg: "bg.accent.blue.lightest",
        },
        icon: {
          color: "icon.accent.blue.light",
        },
        tag: {
          bg: "bg.accent.blue.light",
          color: "text.accent.blue.dark",
          px: "200",
        },
      },
      DEFAULT: {
        root: {
          bg: "bg.lightest",
        },
        icon: {
          color: "icon.light",
        },
        tag: {
          display: "none",
        },
      },
    },
    size: {
      lg: {
        root: {
          h: "280px",
        },
      },
      sm: {
        root: {
          h: "180px",
        },
      },
    },
  },
  defaultVariants: {
    size: "lg",
    visual: "DEFAULT",
  },
});

export const HeaderImage = ({
  image,
  type,
  size = "lg",
  doNotShowFallback = false,
  isLogoImage = false,
  isGrayscale: _isGrayscale = false,
  DefaultIcon,
  smartFill,
}: HeaderImageProps) => {
  const { t: tCore } = useTranslation("core", { keyPrefix: "STAFF" });
  const { root, tag, icon } = defaultImageStyles({ visual: type, size });

  if (!image && !doNotShowFallback) {
    const iconMap: Record<HeaderImageProps["type"], React.ReactNode> = {
      OWNER: <Icon Element={DefaultIcon ?? BoxIso} className={icon} />,
      EDITOR: <Icon Element={DefaultIcon ?? BoxIso} className={icon} />,
      VIEW_ONLY: <Icon Element={DefaultIcon ?? BoxIso} className={icon} />,
      NO_ACCESS: <Icon Element={DefaultIcon ?? BoxIso} className={icon} />,
      DEFAULT: <Icon Element={DefaultIcon ?? BoxIso} className={icon} />,
    };
    return (
      <Flex className={root}>
        {type !== "DEFAULT" && (
          <Flex className={tag}>
            <Text visual={"smallMedium"}>{tCore(type)}</Text>
          </Flex>
        )}
        {iconMap[type]}
      </Flex>
    );
  }

  if (!image && doNotShowFallback && type === "DEFAULT") return null;

  if (!image && doNotShowFallback) {
    return (
      <Flex
        className={tag}
        px={"100!"}
        py={"50!"}
        borderRadius={"50!"}
        position={"static"}
        width={"max-content"}
      >
        <Text visual={"smallTag"}>{tCore(type)}</Text>
      </Flex>
    );
  }

  const imgWidth = size === "lg" ? 440 : 270;
  const imgHeight = size === "lg" ? 280 : 180;

  return (
    <Flex className={root} position="relative">
      <SmartImage
        image={image}
        width={imgWidth}
        height={imgHeight}
        shape="rounded"
        isLogo={isLogoImage}
        smartFill={smartFill}
      />
      <Flex className={tag}>
        <Text visual={"smallMedium"}>{tCore(type)}</Text>
      </Flex>
    </Flex>
  );
};
